import React from 'react';
import {Box, Stack, styled} from '@mui/material';
import {Body, designSystemToken, Icon} from '@lightricks/react-design-system';
import {AssetErrors} from '@contentlab/stores/contentLabStore';
import {FeatureName} from '@contentlab/types/project';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import OnboardingFileUploader from './OnboardingFileUploader';

const IMAGES = {
  BACK: 'https://storage.googleapis.com/brands-public-assets/content-lab/Onboarding/SocialPromo-small.png',
  MIDDLE:
    'https://storage.googleapis.com/brands-public-assets/content-lab/Onboarding/CourseVideo-small.png',
  FRONT:
    'https://storage.googleapis.com/brands-public-assets/content-lab/Onboarding/Commercial-small.png',
};

const StackedImagesContainer = styled(Stack)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  alignItems: 'center',
  position: 'relative',
  width: '75px',
  height: '85px',
}));

const shouldNotForwardProps = ['image', 'size'];

const StackedImage = styled(Box, {
  shouldForwardProp: (prop: string) => !shouldNotForwardProps.includes(prop),
})(({image, size, top}: {image: string; size: number; top: number}) => ({
  position: 'absolute',
  width: `${size}px`,
  height: `${size}px`,
  top: `${top}px`,
  border: '3px solid white',
  borderRadius: '25%',
  background: `url(${image})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  transformOrigin: 'bottom center',
  transition: 'transform 0.3s ease-in-out',
}));

function StackedImages() {
  return (
    <StackedImagesContainer>
      <StackedImage
        className="stacked-image-back"
        image={IMAGES.BACK}
        size={51 - 6}
        top={0}
      />
      <StackedImage
        className="stacked-image-middle"
        image={IMAGES.MIDDLE}
        size={65 - 6}
        top={6}
      />
      <StackedImage
        className="stacked-image-front"
        image={IMAGES.FRONT}
        size={75 - 6}
        top={13}
      />
    </StackedImagesContainer>
  );
}

function OnboardingFeatureModal({
  onClickSampleAssets,
  showBestPractices,
  feature,
  assetErrors,
  isLoading,
}: {
  onClickSampleAssets: () => void;
  showBestPractices: boolean;
  feature: FeatureName | string;
  assetErrors: AssetErrors[];
  isLoading: boolean;
}) {
  return (
    <Stack direction="column" gap="16px">
      <OnboardingFileUploader
        showBestPractices={showBestPractices}
        feature={feature}
        assetErrors={assetErrors}
        analyticsLocation={CONTENT_LAB_VIEW_NAMES.ONBOARDING_UPLOAD_MODAL}
        isLoading={isLoading}
        width="480px"
        height="291px"
      />
      <Box
        sx={{
          'position': 'relative',
          'borderRadius': '16px',
          'cursor': 'pointer',
          // Default border using box-shadow
          'boxShadow': `0 0 0 1px ${designSystemToken(
            'semantic.bg.neutral-inverse'
          )}`,
          'transition': 'box-shadow 0.3s ease-in-out',
          '@keyframes arrowSpring': {
            '0%': {
              transform: 'translateX(0px)',
            },
            '60%': {
              transform: 'translateX(10px)',
            },
            '100%': {
              transform: 'translateX(8px)',
            },
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-1px',
            left: '-1px',
            right: '-1px',
            bottom: '-1px',
            background: 'linear-gradient(to right, #6B5DFF, #D5B4FB, #2B61FF)',
            borderRadius: 'inherit',
            zIndex: -1,
            opacity: 0,
            transition: 'opacity 0.3s ease-in-out',
          },
          '&:hover': {
            'boxShadow': '0 0 0 1px transparent', // Hide default border
            '&::before': {
              opacity: 1,
            },
          },
        }}
        onClick={onClickSampleAssets}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          borderRadius="15px"
          padding="8px 20px 8px 16px"
          sx={{
            'backgroundColor': 'white',
            '.sample-videos-arrow': {
              transition: 'transform 0.3s ease-out',
            },
            '&:hover': {
              '.sample-videos-arrow': {
                transform: 'translateX(8px)',
                animation: 'arrowSpring 0.4s ease-out',
                animationFillMode: 'none',
              },
              '.stacked-image-back': {
                transform: 'translateX(-10px) rotate(-10deg)',
              },
              '.stacked-image-middle': {
                transform: 'translateX(5px) rotate(10deg)',
              },
              '.stacked-image-front': {
                transform: 'scale(0.98)',
              },
            },
          }}
        >
          <Stack direction="row" alignItems="center" gap="12px">
            <StackedImages />
            <Body size="lg" color={designSystemToken('semantic.fg.primary')}>
              {translate('No video? Try a sample clip.')}
            </Body>
          </Stack>
          <Icon
            className="sample-videos-arrow"
            name="Actions-Arrow-Forward-Small"
            size="medium"
            appearance="neutral"
          />
        </Stack>
      </Box>
    </Stack>
  );
}

export default OnboardingFeatureModal;
